import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadSimple,
  CircleNotch,
  CheckCircle,
  XCircle,
  DownloadSimple,
  Trash,
  Stack,
  ArrowUpRight,
  Warning,
  FileArchive,
  FilePng,
} from "@phosphor-icons/react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

const HARD_CAP = 10;

export default function Bulk() {
  const { user, booted } = useAuth();
  const [usage, setUsage] = useState(null);
  const [queue, setQueue] = useState([]); // {id, file, preview, status, result?, error?}
  const [running, setRunning] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [settings, setSettings] = useState({
    mode: "multi",
    quality: "balanced",
    num_colors: 4,
    remove_bg: false,
  });

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/usage/me").then((r) => setUsage(r.data)).catch(() => {});
  }, [user]);

  const bulkLimit = useMemo(() => {
    if (!usage) return HARD_CAP;
    if (usage.source === "admin") return HARD_CAP;
    return Math.min(HARD_CAP, usage?.features?.bulk || 0);
  }, [usage]);

  const bulkAllowed = bulkLimit > 0;

  if (!booted) {
    return (
      <div className="py-32 text-center text-[var(--fg-2)] font-mono text-xs tracking-widest">
        LOADING…
      </div>
    );
  }
  if (user === false || !user) return <Navigate to="/login?next=/bulk" replace />;
  if (usage && !bulkAllowed) return <UpsellGate />;

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    const { accepted, rejected } = triageFiles(incoming);
    if (rejected.length > 0) {
      toast.warning(`${rejected.length} file(s) ignored (unsupported type)`);
    }
    setQueue((q) => {
      const combined = [...q, ...accepted.map(buildItem)];
      if (combined.length > bulkLimit) {
        toast.warning(`Capped at ${bulkLimit} items per batch`);
        return combined.slice(0, bulkLimit);
      }
      return combined;
    });
  };

  const removeItem = (id) => {
    setQueue((q) => q.filter((x) => x.id !== id));
  };

  const clearQueue = () => {
    setQueue([]);
  };

  const runBatch = async () => {
    if (running) return;
    const pending = queue.filter((q) => q.status !== "done");
    if (pending.length === 0) return;
    setRunning(true);
    // Mark all pending as converting so user sees activity immediately.
    setQueue((q) =>
      q.map((x) => (x.status !== "done" ? { ...x, status: "converting", error: null } : x)),
    );
    try {
      const fd = new FormData();
      pending.forEach((item) => fd.append("files", item.file));
      fd.append("mode", settings.mode);
      fd.append("quality", settings.quality);
      fd.append("num_colors", String(settings.num_colors));
      fd.append("remove_bg", settings.remove_bg ? "true" : "false");
      const { data } = await api.post("/convert-bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Bulk takes longer; lift axios' default timeout.
        timeout: 120000,
      });
      // Map server results back onto the queue by filename (order preserved
      // by the backend, but we still key by filename for robustness).
      setQueue((q) => {
        const updated = [...q];
        const results = data?.results || [];
        // If the backend expanded a ZIP, it might return MORE rows than
        // we submitted. Add those as new tiles so the user can see them.
        const known = new Set(updated.map((x) => x.file.name));
        results.forEach((res) => {
          const idx = updated.findIndex(
            (x) => x.file.name === res.filename && x.status !== "done",
          );
          if (idx !== -1) {
            updated[idx] = applyResult(updated[idx], res);
          } else if (!known.has(res.filename)) {
            // Synthesise a tile for files that came out of a ZIP.
            updated.push(syntheticTile(res));
          }
        });
        return updated;
      });
      toast.success(
        `Batch complete — ${data.successes} converted, ${data.failures} failed`,
      );
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setQueue((q) =>
        q.map((x) => (x.status === "converting" ? { ...x, status: "failed", error: msg } : x)),
      );
    } finally {
      setRunning(false);
      // Refresh usage
      api.get("/usage/me").then((r) => setUsage(r.data)).catch(() => {});
    }
  };

  const downloadItem = (item) => {
    if (!item.result?.svg) return;
    const blob = new Blob([item.result.svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = item.file.name.replace(/\.[^.]+$/, "") + ".svg";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllZip = async () => {
    const done = queue.filter((x) => x.status === "done" && x.result?.svg);
    if (done.length === 0) return;
    setZipping(true);
    try {
      const items = done.map((x) => ({
        filename: x.file.name,
        svg: x.result.svg,
      }));
      const token = localStorage.getItem("decal_token");
      // Use fetch to get a real Blob (axios-blob setups can be fiddly).
      const resp = await fetch(`${API}/bulk-zip`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          items,
          archive_name: `decal-bulk-${new Date().toISOString().slice(0, 10)}`,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `decal-bulk-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${done.length} SVGs as a zip`);
    } catch (e) {
      toast.error(e.message || "Zip download failed");
    } finally {
      setZipping(false);
    }
  };

  const doneCount = queue.filter((x) => x.status === "done").length;
  const pendingCount = queue.filter((x) => x.status !== "done").length;

  return (
    <div className="max-w-[1440px] mx-auto" data-testid="bulk-page">
      <div className="border-b border-[var(--border)] px-6 md:px-10 py-10 md:py-14">
        <div className="micro-label flex items-center gap-2">
          <Stack size={12} weight="bold" /> / Bulk convert
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-2">
          Convert up to {bulkLimit} images at once
        </h1>
        <p className="text-[var(--fg-2)] mt-3 max-w-lg">
          Drop a batch — or even a zip of PNGs. One trip to the server, one zip
          back, every image forced under the 15 KB ceiling.
        </p>
      </div>

      {/* Settings bar */}
      <div className="border-b border-[var(--border)] bg-white p-4 md:p-5 flex flex-wrap items-end gap-4">
        <SettingSelect
          label="Mode"
          value={settings.mode}
          options={[
            { v: "bw", l: "B/W" },
            { v: "single", l: "Single" },
            { v: "multi", l: "Multi" },
          ]}
          onChange={(v) => setSettings({ ...settings, mode: v })}
          testid="bulk-mode"
        />
        <SettingSelect
          label="Quality"
          value={settings.quality}
          options={[
            { v: "fast", l: "Fast" },
            { v: "balanced", l: "Balanced" },
            { v: "high", l: "High" },
          ]}
          onChange={(v) => setSettings({ ...settings, quality: v })}
          testid="bulk-quality"
        />
        {settings.mode === "multi" && (
          <SettingSelect
            label="Colors"
            value={String(settings.num_colors)}
            options={[2, 3, 4, 5, 6, 7, 8].map((n) => ({ v: String(n), l: String(n) }))}
            onChange={(v) => setSettings({ ...settings, num_colors: Number(v) })}
            testid="bulk-colors"
          />
        )}
        <label className="flex items-center gap-2" data-testid="bulk-remove-bg">
          <input
            type="checkbox"
            checked={settings.remove_bg}
            onChange={(e) => setSettings({ ...settings, remove_bg: e.target.checked })}
          />
          <span className="micro-label">Remove BG</span>
        </label>
        <div className="ml-auto flex items-center gap-2">
          <span className="chip" data-testid="bulk-counter">
            {queue.length}/{bulkLimit} QUEUED
          </span>
          {queue.length > 0 && (
            <button
              onClick={clearQueue}
              className="btn-ghost"
              data-testid="btn-bulk-clear"
            >
              <Trash size={12} weight="bold" /> Clear
            </button>
          )}
          <button
            onClick={runBatch}
            disabled={running || pendingCount === 0}
            className="btn-primary"
            data-testid="btn-bulk-run"
          >
            {running ? (
              <>
                <CircleNotch size={14} className="animate-spin" weight="bold" /> Running
              </>
            ) : (
              <>
                Run batch <CheckCircle size={14} weight="bold" />
              </>
            )}
          </button>
          <button
            onClick={downloadAllZip}
            disabled={zipping || doneCount === 0}
            className="btn-accent"
            data-testid="btn-bulk-download-zip"
          >
            {zipping ? (
              <>
                <CircleNotch size={14} className="animate-spin" weight="bold" /> Zipping
              </>
            ) : (
              <>
                <FileArchive size={14} weight="bold" /> Download .zip
              </>
            )}
          </button>
        </div>
      </div>

      {/* Dropzone */}
      <div className="p-6 md:p-10">
        <BulkDrop
          onFiles={addFiles}
          disabled={queue.length >= bulkLimit}
          limit={bulkLimit}
          remaining={bulkLimit - queue.length}
        />
      </div>

      {/* Queue grid */}
      {queue.length > 0 && (
        <div className="px-6 md:px-10 pb-16">
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-px bg-[var(--border)] border border-[var(--border)]"
            data-testid="bulk-queue"
          >
            {queue.map((it) => (
              <BulkTile
                key={it.id}
                item={it}
                onRemove={() => removeItem(it.id)}
                onDownload={() => downloadItem(it)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- building blocks ----------
function triageFiles(files) {
  const accepted = [];
  const rejected = [];
  files.forEach((f) => {
    const name = (f.name || "").toLowerCase();
    const isZip = name.endsWith(".zip") || f.type === "application/zip";
    const isImg = (f.type || "").startsWith("image/");
    if (isZip || isImg) accepted.push(f);
    else rejected.push(f);
  });
  return { accepted, rejected };
}

function buildItem(file) {
  const isZip =
    (file.name || "").toLowerCase().endsWith(".zip") ||
    file.type === "application/zip";
  return {
    id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    preview: isZip ? null : URL.createObjectURL(file),
    isZip,
    status: "pending", // pending | converting | done | failed
  };
}

function applyResult(item, res) {
  if (res.ok) {
    return { ...item, status: "done", result: res, error: null };
  }
  return { ...item, status: "failed", error: res.error || "Conversion failed" };
}

function syntheticTile(res) {
  const fake = new File([""], res.filename, { type: "image/svg+xml" });
  return {
    id: `${res.filename}-${Math.random().toString(36).slice(2, 8)}`,
    file: fake,
    preview: null,
    isZip: false,
    status: res.ok ? "done" : "failed",
    result: res.ok ? res : undefined,
    error: res.ok ? null : res.error,
    fromZip: true,
  };
}

function BulkDrop({ onFiles, disabled, limit, remaining }) {
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        if (!disabled) onFiles(e.dataTransfer.files);
      }}
      className={`relative flex flex-col items-center justify-center cursor-pointer border-2 border-dashed transition-colors py-14 px-6 text-center ${
        disabled
          ? "border-[var(--border)] bg-[var(--bg-2)] opacity-60 cursor-not-allowed"
          : drag
          ? "border-[var(--accent)] bg-[#fff5f2]"
          : "border-[var(--border)] bg-white hover:border-black"
      }`}
      data-testid="bulk-dropzone"
    >
      <input
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,application/zip,.zip"
        className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
        disabled={disabled}
        onChange={(e) => onFiles(e.target.files)}
        data-testid="bulk-dropzone-input"
      />
      <div className="h-12 w-12 border border-black flex items-center justify-center mb-5 bg-white">
        <UploadSimple size={22} weight="bold" />
      </div>
      <div className="font-display font-bold text-xl md:text-2xl">
        {disabled
          ? `Batch full (${limit} max)`
          : `Drop up to ${remaining} image${remaining === 1 ? "" : "s"}`}
      </div>
      <div className="mt-2 text-[var(--fg-2)] text-sm">
        PNG · JPG · WEBP · or a <strong>.zip of images</strong> · click or drag
      </div>
    </div>
  );
}

function BulkTile({ item, onRemove, onDownload }) {
  const isZipQueued = item.isZip && item.status === "pending";
  return (
    <div className="bg-white relative" data-testid={`bulk-tile-${item.id}`}>
      <div className="checker aspect-square flex items-center justify-center p-3 relative">
        {item.result?.svg ? (
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(item.result.svg)}`}
            alt="svg"
            className="max-h-full max-w-full object-contain m-auto block"
          />
        ) : isZipQueued ? (
          <div className="flex flex-col items-center gap-2 text-[var(--fg-2)]">
            <FileArchive size={36} weight="bold" />
            <span className="font-mono text-[10px] tracking-widest">ZIP — expands on run</span>
          </div>
        ) : item.preview ? (
          <img
            src={item.preview}
            alt={item.file.name}
            className="max-h-full max-w-full object-contain opacity-70"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-[var(--fg-2)]">
            <FilePng size={36} weight="bold" />
            <span className="font-mono text-[10px] tracking-widest">FROM ZIP</span>
          </div>
        )}
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 h-6 w-6 bg-white border border-[var(--border)] flex items-center justify-center hover:border-[var(--accent)] hover:text-[var(--accent)]"
          data-testid={`bulk-remove-${item.id}`}
          title="Remove"
        >
          <Trash size={12} weight="bold" />
        </button>
        <div className="absolute top-2 left-2">
          <StatusBadge status={item.status} />
        </div>
      </div>
      <div className="p-3 border-t border-[var(--border)] text-xs font-mono flex items-center justify-between">
        <span className="truncate" title={item.file.name}>{item.file.name}</span>
        {item.result?.size_bytes ? (
          <span>{(item.result.size_bytes / 1024).toFixed(1)}KB</span>
        ) : item.file?.size ? (
          <span className="text-[var(--fg-2)]">{(item.file.size / 1024).toFixed(0)}KB</span>
        ) : null}
      </div>
      <div className="p-3 border-t border-[var(--border)] flex items-center justify-between">
        {item.error ? (
          <span className="text-[10px] text-[var(--accent)] truncate pr-2" title={item.error}>
            {item.error}
          </span>
        ) : (
          <span className="text-[10px] font-mono text-[var(--fg-2)] tracking-wider">
            {item.status.toUpperCase()}
            {item.result?.compatible === false && " · OVER 15KB"}
          </span>
        )}
        <button
          onClick={onDownload}
          disabled={item.status !== "done"}
          className="btn-ghost px-2 py-1 text-[10px]"
          data-testid={`bulk-download-${item.id}`}
        >
          <DownloadSimple size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { ico: <UploadSimple size={12} weight="bold" />, bg: "#f4f4f5", color: "#0a0a0a", label: "QUEUE" },
    converting: { ico: <CircleNotch size={12} className="animate-spin" weight="bold" />, bg: "#fff5f2", color: "#ff2e00", label: "TRACE" },
    done: { ico: <CheckCircle size={12} weight="fill" />, bg: "#eefbf2", color: "#00c853", label: "DONE" },
    failed: { ico: <XCircle size={12} weight="fill" />, bg: "#fff5f2", color: "#ff2e00", label: "FAIL" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono tracking-widest border"
      style={{ background: s.bg, color: s.color, borderColor: s.color }}
    >
      {s.ico}
      {s.label}
    </span>
  );
}

function SettingSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="flex items-center gap-2" data-testid={testid}>
      <span className="micro-label">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 px-2 border border-[var(--border)] font-mono text-xs bg-white"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  );
}

function UpsellGate() {
  return (
    <div className="max-w-[720px] mx-auto py-20 px-6 text-center" data-testid="bulk-upsell">
      <div className="h-16 w-16 border border-black flex items-center justify-center mx-auto">
        <Warning size={28} weight="bold" className="text-[var(--accent)]" />
      </div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-6">
        Bulk convert is a Pro feature
      </h1>
      <p className="text-[var(--fg-2)] mt-3 max-w-md mx-auto">
        Upgrade to the Pro tier to run up to 10 conversions in a single batch,
        plus unlimited monthly conversions and priority processing.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/pricing" className="btn-accent" data-testid="btn-bulk-see-pricing">
          See Pro pricing <ArrowUpRight size={14} weight="bold" />
        </Link>
        <Link to="/convert" className="btn-ghost" data-testid="btn-bulk-back-convert">
          Back to converter
        </Link>
      </div>
    </div>
  );
}
